// src/components/views/JewelleryView.tsx - PREMIUM JEWELLERY MARKETPLACE
import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useLocation, NavLink } from 'react-router-dom';
import {
  ArrowLeft, Search, ShoppingBag, Bell, MessageCircle,
  Filter, Heart, X, User, Clock,
  Gem, Sparkles, Layers, Palette, Crown, Users,
  Gavel, Timer, DollarSign, Award, Shield, Diamond,
  Eye, TrendingUp, Calendar,
  Info, Settings, Hash, Scale, Ruler, Weight, Tag,
  ChevronDown, ChevronUp, Menu, Plus, Minus, Home,
  ShoppingCart, ExternalLink, Share2, Flag, ArrowRight, PenTool,
  Loader2, Star, StarHalf, StarOff
} from 'lucide-react';
import { db } from '../../services/firebase';
import {
  collection, query, where, onSnapshot, doc, getDoc, getDocs,
} from 'firebase/firestore';
import { useAuth } from '../../hooks/useAuth';
import { useCart } from '../../contexts/CartContext';

import { CartOverlay } from '../shopping/CartOverlay';
import { JewelleryProductDetailModal } from '../shopping/JewelleryProductDetailModal';
import { JewelleryProfileModal } from '../shopping/JewelleryProfileModal';
import { PaymentModal } from '../shopping/PaymentModal';

// 🟢 STUDIO — the custom design builder
import { JewelleryStudio, StudioDesignPayload } from '../shopping/JewelleryStudio';

// 🔑 The house atelier. Custom designs are attributed to this seller ID.
// Make sure `users/scoop-and-stone-atelier` exists in Firestore with
// `deliveryMethods` populated, or the PaymentModal routing step will be empty.
const ATELIER_ID = 'scoop-and-stone-atelier';
const ATELIER_NAME = 'Scoop & Stone Atelier';

// 🟢 CUSTOM SVG ICONS
const RingIcon = ({ stroke = "#17171A", size = 64, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 100 100" fill="none" stroke={stroke} strokeWidth="2" className={className}>
    <circle cx="50" cy="60" r="26" />
    <path d="M39 36 L50 14 L61 36 L53 42 L47 42 Z" fill={stroke} stroke="none" />
  </svg>
);

const NecklaceIcon = ({ stroke = "#17171A", size = 64, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 100 100" fill="none" stroke={stroke} strokeWidth="2" className={className}>
    <path d="M18 18 Q50 68 82 18" />
    <path d="M43 60 L50 76 L57 60 Z" fill={stroke} stroke="none" />
  </svg>
);

const BraceletIcon = ({ stroke = "#17171A", size = 64, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 100 100" fill="none" stroke={stroke} strokeWidth="2" className={className}>
    <ellipse cx="50" cy="55" rx="32" ry="17" />
    <circle cx="50" cy="38" r="5" fill={stroke} stroke="none" />
  </svg>
);

const WatchIcon = ({ stroke = "#17171A", size = 64, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 100 100" fill="none" stroke={stroke} strokeWidth="2" className={className}>
    <path d="M40 20 h20 v14 h-20 z" />
    <path d="M40 66 h20 v14 h-20 z" />
    <circle cx="50" cy="50" r="24" />
    <path d="M50 36 v14 l10 8" strokeLinecap="round" />
  </svg>
);

const EarringIcon = ({ stroke = "#17171A", size = 64, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 100 100" fill="none" stroke={stroke} strokeWidth="2" className={className}>
    <circle cx="50" cy="20" r="7" />
    <path d="M50 27 v12" />
    <path d="M36 39 h28 l-7 38 a7 7 0 0 1 -14 0 Z" />
  </svg>
);

const WaxSeal = ({ size = 40, className = "" }) => (
  <div className={`relative ${className}`} style={{ width: size, height: size }}>
    <svg width={size} height={size} viewBox="0 0 100 100">
      <circle cx="50" cy="50" r="46" fill="#7A1F2B" />
      <circle cx="50" cy="50" r="46" fill="none" stroke="#A6812C" strokeWidth="2" />
      <circle cx="50" cy="50" r="38" fill="none" stroke="#A6812C" strokeWidth="1" opacity="0.6" />
      <text x="50" y="46" textAnchor="middle" fill="#F4E9C9" fontSize="11" fontFamily="Georgia, serif" letterSpacing="1">
        SCOOP
      </text>
      <text x="50" y="60" textAnchor="middle" fill="#F4E9C9" fontSize="8" fontFamily="Georgia, serif" letterSpacing="2">
        VERIFIED
      </text>
    </svg>
  </div>
);

const GavelIcon = ({ size = 14, stroke = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="2" strokeLinecap="round">
    <path d="M14 5 L19 10" />
    <path d="M9.5 9.5 L4 15 L6 17 L11.5 11.5" />
    <path d="M13 8 L16 11" />
    <path d="M2 20 h9" />
    <path d="M16 3 L21 8" />
  </svg>
);

interface JewelleryItem {
  id: string;
  type: 'jewellery';
  itemName: string;
  price: number;
  originalPrice?: number;
  image: string;
  images?: string[];
  condition: string;
  size: string;
  category: string;
  styleCategory?: string;
  userName: string;
  userId: string;
  userAvatar?: string;
  sellerName?: string;
  sellerId?: string;
  workspaceId?: string;
  brandName?: string;
  status?: string;
  stock?: number;
  hype: number;
  createdAt: any;
  description?: string;
  location?: string;
  color?: string;
  likes?: string[];
  comments?: number;
  authenticationBadge?: boolean;
  isOneOfOne?: boolean;

  hallmark?: string;
  metalType?: string;
  material?: string;
  jewelleryType?: 'fine' | 'fashion' | 'both';
  certificate?: {
    isCertified: boolean;
    issuer: string;
    number: string;
  };
  weight?: number;
  weightUnit?: string;
  gemstone?: string;
  gemstoneWeight?: number;
  gemstoneClarity?: string;
  gemstoneColour?: string;
  caratWeight?: number;
  totalCaratWeight?: number;
  ringSize?: string;
  chainLength?: string;
  braceletLength?: string;
  clasp?: string;
  clarity?: string;
  colourGrade?: string;
  cut?: string;
  origin?: string;
  setting?: string;

  isAuction?: boolean;
  startingBid?: number;
  reservePrice?: number;
  bidIncrement?: number;
  auctionEndDate?: number;
  currentBid?: number;
  numberOfBids?: number;
  highestBidderId?: string;
  auctionStatus?: 'draft' | 'active' | 'ended' | 'sold';
}

interface JewelleryViewProps {
  wishlist?: string[];
  onToggleWishlist?: (id: string, type: 'product') => void;
  onSelectProduct?: (product: any) => void;
  onViewProfile?: (userId: string) => void;
  onSendMessage?: (sellerId: string, sellerName: string) => void;
}

export const JewelleryView: React.FC<JewelleryViewProps> = ({
  wishlist = [],
  onToggleWishlist,
  onSelectProduct,
  onViewProfile,
  onSendMessage
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user: currentUser, profile: userProfile } = useAuth();
  const { cartItems, addToCart, removeFromCart, updateQuantity, clearCart } = useCart();

  const [items, setItems] = useState<JewelleryItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('All');
  const [activeFilter, setActiveFilter] = useState<'all' | 'auction' | 'buy_now'>('all');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState({
    minPrice: 0,
    maxPrice: 5000000,
    metalType: '',
    gemstone: '',
    condition: ''
  });
  const [selectedItem, setSelectedItem] = useState<JewelleryItem | null>(null);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [viewingJeweller, setViewingJeweller] = useState<any>(null);
  const [jewellerProducts, setJewellerProducts] = useState<any[]>([]);
  const [isProfileLoading, setIsProfileLoading] = useState(false);

  // Payment modal state
  const [isPaymentOpen, setIsPaymentOpen] = useState(false);
  const [pendingBuyNowItem, setPendingBuyNowItem] = useState<any>(null);

  // 🟢 Studio state
  const [isStudioOpen, setIsStudioOpen] = useState(false);

  const categories = [
    { name: 'All', icon: <Gem size={16} /> },
    { name: 'Ring', icon: <RingIcon size={16} stroke="currentColor" /> },
    { name: 'Necklace', icon: <NecklaceIcon size={16} stroke="currentColor" /> },
    { name: 'Earrings', icon: <EarringIcon size={16} stroke="currentColor" /> },
    { name: 'Bracelet', icon: <BraceletIcon size={16} stroke="currentColor" /> },
    { name: 'Brooch', icon: <Award size={16} /> },
    { name: 'Set', icon: <Layers size={16} /> },
  ];

  const metalTypes = ['Gold', 'Silver', 'Platinum', 'Rose Gold', 'White Gold', 'Yellow Gold'];
  const gemstones = ['Diamond', 'Sapphire', 'Ruby', 'Emerald', 'Amethyst', 'Topaz', 'Quartz'];
  const conditions = ['New', 'Excellent', 'Very Good', 'Good', 'Fair', 'Vintage'];

  /* ============================================================
     FETCH JEWELLERY ITEMS
     ============================================================ */
  useEffect(() => {
    const q = query(
      collection(db, 'products'),
      where('workspaceType', '==', 'jewellery'),
      where('status', 'in', ['active', 'sold'])
    );

    const unsubscribe = onSnapshot(q, async (snapshot) => {
      try {
        const itemsList = await Promise.all(snapshot.docs.map(async (docSnap) => {
          const data = docSnap.data();
          const userId = data.workspaceId || data.sellerId || data.userId || data.businessId;

          let sellerName = 'Unknown Jeweller';
          let sellerId = userId || '';
          let sellerAvatar = '';

          if (data.sellerName && data.sellerName !== 'Unknown Seller') {
            sellerName = data.sellerName;
          } else if (data.brandName) {
            sellerName = data.brandName;
          } else if (data.businessName) {
            sellerName = data.businessName;
          } else if (userId) {
            try {
              const userDoc = await getDoc(doc(db, 'users', userId));
              if (userDoc.exists()) {
                const userData = userDoc.data();
                sellerName = userData.displayName || userData.businessName || userData.username || 'Jeweller';
                sellerAvatar = userData.photoURL || userData.logoUrl || '';
              }
            } catch (e) { console.error('Error fetching user:', e); }
          }

          return {
            id: docSnap.id,
            type: 'jewellery',
            itemName: data.name || data.itemName || 'Untitled',
            price: data.price || 0,
            image: data.image || (data.images && data.images[0]) || '',
            images: data.images || [],
            condition: data.condition || 'New',
            size: data.size || data.stockVariants?.[0]?.size || '',
            category: data.category || 'Ring',
            styleCategory: data.styleCategory || '',
            userName: sellerName,
            userId: sellerId,
            userAvatar: sellerAvatar,
            sellerName: sellerName,
            sellerId: sellerId,
            sellerAvatar: sellerAvatar,
            workspaceId: userId,
            brandName: data.brandName,
            status: data.status || 'active',
            stock: data.stock || 1,
            hype: data.hype || Math.floor(Math.random() * 50) + 1,
            createdAt: data.createdAt,
            description: data.description || data.caption,
            location: data.location,
            color: data.color || data.stockVariants?.[0]?.color || '',
            authenticationBadge: data.authenticationBadge || false,
            isOneOfOne: data.isOneOfOne || true,

            hallmark: data.hallmark,
            metalType: data.metalType,
            material: data.material,
            jewelleryType: data.jewelleryType || 'fine',
            certificate: data.certificate,
            weight: data.weight,
            weightUnit: data.weightUnit,
            gemstone: data.gemstone,
            gemstoneWeight: data.gemstoneWeight,
            gemstoneClarity: data.gemstoneClarity,
            gemstoneColour: data.gemstoneColour,
            caratWeight: data.caratWeight,
            totalCaratWeight: data.totalCaratWeight,
            ringSize: data.ringSize,
            chainLength: data.chainLength,
            braceletLength: data.braceletLength,
            clasp: data.clasp,
            clarity: data.clarity,
            colourGrade: data.colourGrade,
            cut: data.cut,
            origin: data.origin,
            setting: data.setting,

            isAuction: data.isAuction || false,
            startingBid: data.startingBid,
            reservePrice: data.reservePrice,
            bidIncrement: data.bidIncrement,
            auctionEndDate: data.auctionEndDate,
            currentBid: data.currentBid,
            numberOfBids: data.numberOfBids || 0,
            highestBidderId: data.highestBidderId,
            auctionStatus: data.auctionStatus || 'draft',
          } as JewelleryItem;
        }));

        itemsList.sort((a, b) => {
          const dateA = a.createdAt?.toMillis ? a.createdAt.toMillis() : (a.createdAt?.seconds ? a.createdAt.seconds * 1000 : (a.createdAt || 0));
          const dateB = b.createdAt?.toMillis ? b.createdAt.toMillis() : (b.createdAt?.seconds ? b.createdAt.seconds * 1000 : (b.createdAt || 0));
          return dateB - dateA;
        });

        setItems(itemsList);
        setIsLoading(false);
      } catch (error) {
        console.error('Error processing jewellery items:', error);
        setIsLoading(false);
      }
    }, (error) => {
      console.error("🔥 FIRESTORE ERROR:", error);
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, []);

  /* ============================================================
     FILTER
     ============================================================ */
  const filteredItems = useMemo(() => {
    return items.filter(item => {
      const matchesCategory = activeCategory === 'All' || item.category === activeCategory;
      const matchesFilter = activeFilter === 'all' ||
                             (activeFilter === 'auction' && item.isAuction) ||
                             (activeFilter === 'buy_now' && !item.isAuction);
      const price = item.isAuction ? (item.currentBid || item.startingBid || 0) : item.price;
      const matchesPrice = price >= filters.minPrice && price <= filters.maxPrice;
      const matchesMetal = !filters.metalType || item.metalType === filters.metalType;
      const matchesGemstone = !filters.gemstone || item.gemstone === filters.gemstone;
      const matchesCondition = !filters.condition || item.condition === filters.condition;

      return matchesCategory && matchesFilter && matchesPrice &&
             matchesMetal && matchesGemstone && matchesCondition;
    });
  }, [items, activeCategory, activeFilter, filters]);

  /* ============================================================
     HELPERS
     ============================================================ */
  const showToast = (message: string) => {
    window.dispatchEvent(new CustomEvent('show-toast', { detail: { message } }));
  };

  /**
   * Turn a studio payload into a JewelleryItem (shop-shaped object).
   * Custom designs are always attributed to the house atelier.
   */
  const buildCustomItemFromDesign = (design: StudioDesignPayload): JewelleryItem => {
    const prettyPiece = design.piece.charAt(0).toUpperCase() + design.piece.slice(1);
    const now = Date.now();
    return {
      id: `custom_${now}`,
      type: 'jewellery',
      itemName: `Custom ${prettyPiece}`,
      price: design.totalPrice,
      image: design.previewDataURL,
      images: [design.previewDataURL],
      condition: 'New',
      size: design.length + ' cm',
      color: design.stringColor,
      category: prettyPiece,
      styleCategory: 'Custom',
      userName: ATELIER_NAME,
      userId: ATELIER_ID,
      sellerName: ATELIER_NAME,
      sellerId: ATELIER_ID,
      workspaceId: ATELIER_ID,
      brandName: 'Scoop & Stone',
      status: 'active',
      stock: 1,
      hype: 0,
      createdAt: now,
      description:
        `${design.length} cm ${design.piece} on ${design.string} (${design.stringColor}) ` +
        `with ${design.beads.length} bead${design.beads.length === 1 ? '' : 's'}.`,
      authenticationBadge: true,
      isOneOfOne: true,
      isAuction: false,
      metalType: design.metalType,
      gemstone: design.gemstone,
      ...({ customDesign: design } as any),
    };
  };

  /**
   * Build a complete CartItem from a JewelleryItem.
   * Custom designs (id starts with "custom_") are always attributed to
   * the house atelier, regardless of what the item says.
   */
  const buildCartItem = (item: any) => {
    const isCustom = typeof item?.id === 'string' && item.id.startsWith('custom_');
    const sellerId   = isCustom ? ATELIER_ID   : (item.sellerId || item.userId || '');
    const sellerName = isCustom ? ATELIER_NAME : (item.sellerName || item.brandName || 'Seller');
    const now = Date.now();

    return {
      cartId: `cart_${item.id}_${now}_${Math.random().toString(36).slice(2, 6)}`,
      id: item.id,
      name: item.itemName,
      price: item.isAuction
        ? (item.currentBid || item.startingBid || item.price)
        : item.price,
      image: item.image,
      images: item.images || [],
      quantity: 1,

      // Seller — what CartOverlay + PaymentModal key off
      sellerId,
      sellerName,
      brand: sellerName,
      businessId: sellerId,
      userId: sellerId,
      workspaceId: sellerId,

      // What CartOverlay renders under the product name
      selectedSize: item.size,
      selectedColor: item.color,

      type: 'jewellery',
      workspaceType: 'jewellery',
      condition: item.condition || 'New',
      hallmark: item.hallmark,
      metalType: item.metalType,
      gemstone: item.gemstone,
      authenticationBadge: item.authenticationBadge,
      isOneOfOne: item.isOneOfOne ?? true,
      stock: item.stock || 1,

      originalItem: item,
      ...(isCustom ? { customDesign: (item as any).customDesign } : {}),
    };
  };

  /* ============================================================
     HANDLERS
     ============================================================ */
  const handleAddToCart = (item: any) => {
    const cartItem = buildCartItem(item);
    addToCart(cartItem);
    showToast('Added to cart! 🛒');
  };

  const handleBuyNow = (item: any) => {
    clearCart();
    const cartItem = buildCartItem(item);
    addToCart(cartItem);
    setPendingBuyNowItem(item);
    setSelectedItem(null);
    setIsPaymentOpen(true);
  };

  const handleCheckoutComplete = (details: any) => {
    console.log('Checkout complete:', details);
    setIsPaymentOpen(false);
    setPendingBuyNowItem(null);
    showToast('🎉 Order placed successfully!');
    setTimeout(() => { clearCart(); }, 1000);
  };

  const handleViewJeweller = async (id: string) => {
    if (!id) return;
    setIsProfileLoading(true);
    try {
      const userDoc = await getDoc(doc(db, 'users', id));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setViewingJeweller({
          id: userDoc.id,
          uid: userDoc.id,
          ...userData,
          businessName: userData.businessName || userData.displayName || 'Scoop & Stone',
          displayName: userData.displayName || userData.businessName || 'Scoop & Stone',
          location: userData.location || 'Global',
          photoURL: userData.photoURL || userData.logoUrl || null,
          logoUrl: userData.logoUrl || userData.photoURL || null,
          bannerUrl: userData.bannerUrl || null,
          bio: userData.description || userData.bio || '',
          verified: userData.verified || false,
          followersCount: userData.followersCount || userData.followers || 0,
        });

        const productsQuery = query(
          collection(db, 'products'),
          where('workspaceId', '==', id)
        );
        const productsSnap = await getDocs(productsQuery);
        setJewellerProducts(productsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      }
    } catch (error) {
      console.error('Error fetching jeweller:', error);
    } finally {
      setIsProfileLoading(false);
    }
  };

  const handlePlaceBid = (item: JewelleryItem, amount: number) => {
    showToast(`Bid of R${amount} placed! 🏛️`);
  };

  /* ============================================================
     🟢 STUDIO HANDLERS

     The studio's own "Add to cart" / "Buy now" buttons fire these.
     Nothing here opens the product detail modal — the user has
     already decided inside the studio.
     ============================================================ */

  /* User clicked "Add to cart" in the studio */
  const handleStudioAddToCart = (design: StudioDesignPayload) => {
    if (!design.beads.length || design.totalPrice <= 0) return;

    const customItem = buildCustomItemFromDesign(design);
    const cartItem = buildCartItem(customItem);

    addToCart(cartItem);
    setIsStudioOpen(false);
    setIsCartOpen(true);                    // show the cart so the user sees it
    showToast('🎨 Custom design added to cart');
  };

  /* User clicked "Buy now" in the studio */
  const handleStudioBuyNow = (design: StudioDesignPayload) => {
    if (!design.beads.length || design.totalPrice <= 0) return;

    const customItem = buildCustomItemFromDesign(design);
    const cartItem = buildCartItem(customItem);

    clearCart();                            // fresh cart for a single-item checkout
    addToCart(cartItem);
    setIsStudioOpen(false);
    setPendingBuyNowItem(customItem);
    setIsPaymentOpen(true);                 // straight to payment
  };

  /* ============================================================
     PRODUCT CARD
     ============================================================ */
  const renderProductCard = (item: JewelleryItem) => {
    const isSold = item.status === 'sold' || item.stock === 0;
    const displayPrice = item.isAuction ? (item.currentBid || item.startingBid || item.price) : item.price;
    const isWishlisted = wishlist.includes(item.id);
    const isAuctionActive = item.isAuction && item.auctionStatus === 'active';

    const getCategoryIcon = () => {
      switch (item.category) {
        case 'Ring': return <RingIcon stroke="#17171A" size={52} />;
        case 'Earrings': return <EarringIcon stroke="#17171A" size={52} />;
        case 'Necklace': return <NecklaceIcon stroke="#17171A" size={52} />;
        case 'Bracelet': return <BraceletIcon stroke="#17171A" size={52} />;
        default: return <Gem size={52} className="text-[#6B6B63]" />;
      }
    };

    return (
      <div
        key={item.id}
        className="bg-white border border-[#E8E0D8] hover:shadow-xl transition-all duration-300 group cursor-pointer hover:-translate-y-1"
        onClick={() => setSelectedItem(item)}
      >
        <div className="relative aspect-square bg-[#FAF8F3] overflow-hidden flex items-center justify-center">
          {item.image ? (
            <img
              src={item.image}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              alt={item.itemName}
            />
          ) : (getCategoryIcon())}

          <div className="absolute top-3 left-3 flex flex-col gap-1">
            {item.authenticationBadge && <WaxSeal size={28} />}
            {item.isAuction && isAuctionActive && (
              <span className="px-2 py-0.5 bg-[#7A1F2B] text-[#F4E9C9] text-[8px] font-['JetBrains_Mono',monospace] font-bold rounded-full flex items-center gap-1 border border-[#A6812C]">
                <GavelIcon size={10} /> Auction
              </span>
            )}
            {item.certificate?.isCertified && (
              <span className="px-2 py-0.5 bg-[#A6812C] text-white text-[8px] font-['JetBrains_Mono',monospace] font-bold rounded-full flex items-center gap-1">
                <Award size={10} /> Certified
              </span>
            )}
          </div>

          {isSold && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
              <span className="text-white font-['JetBrains_Mono',monospace] font-bold text-sm uppercase tracking-widest border-2 border-white px-4 py-1.5 rotate-[-15deg]">
                SOLD
              </span>
            </div>
          )}

          {item.isAuction && isAuctionActive && item.auctionEndDate && (
            <div className="absolute bottom-3 left-3 bg-[#7A1F2B]/90 backdrop-blur-sm px-3 py-1 rounded-full border border-[#A6812C]">
              <div className="flex items-center gap-2 text-[#F4E9C9] text-[9px] font-['JetBrains_Mono',monospace] font-bold">
                <Clock size={12} className="text-[#A6812C]" />
                <AuctionBadge endsAt={item.auctionEndDate} />
              </div>
            </div>
          )}

          <button
            onClick={(e) => { e.stopPropagation(); onToggleWishlist?.(item.id, 'product'); }}
            className="absolute top-3 right-3 p-2 bg-white/90 backdrop-blur-sm rounded-full hover:scale-110 transition-all shadow-lg border border-white/20"
          >
            <Heart size={16} fill={isWishlisted ? 'currentColor' : 'none'} className={isWishlisted ? 'text-[#7A1F2B]' : 'text-[#6B6B63]'} />
          </button>
        </div>

        <div className="p-4">
          <div className="flex items-start justify-between gap-2 mb-1">
            <h3 className="font-['Cormorant_Garamond',serif] text-base font-medium text-[#17171A] truncate flex-1">
              {item.itemName}
            </h3>
            <span className="text-[9px] font-['JetBrains_Mono',monospace] font-bold text-[#A6812C] uppercase tracking-wider whitespace-nowrap">
              {item.condition || 'New'}
            </span>
          </div>

          <div className="flex flex-wrap gap-1 mb-2">
            {item.hallmark && (
              <span className="text-[7px] font-['JetBrains_Mono',monospace] font-bold bg-[#A6812C]/10 text-[#A6812C] px-2 py-0.5 rounded-full border border-[#A6812C]/20">
                {item.hallmark}
              </span>
            )}
            {item.metalType && (
              <span className="text-[7px] font-['JetBrains_Mono',monospace] font-bold bg-[#FAF8F3] text-[#6B6B63] px-2 py-0.5 rounded-full border border-[#E8E0D8]">
                {item.metalType}
              </span>
            )}
            {item.gemstone && (
              <span className="text-[7px] font-['JetBrains_Mono',monospace] font-bold bg-purple-100 text-purple-600 px-2 py-0.5 rounded-full border border-purple-200">
                {item.gemstone}
              </span>
            )}
          </div>

          <p className="text-[10px] font-['Inter',sans-serif] text-[#6B6B63] truncate flex items-center gap-1">
            <User size={10} /> {item.sellerName}
          </p>

          <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#E8E0D8]">
            <div>
              {item.isAuction ? (
                <>
                  <span className="text-[8px] font-['JetBrains_Mono',monospace] font-bold text-[#6B6B63] uppercase tracking-wider">Current Bid</span>
                  <p className="font-['JetBrains_Mono',monospace] font-bold text-[#A6812C]">R{displayPrice.toFixed(2)}</p>
                  <span className="text-[8px] font-['JetBrains_Mono',monospace] text-[#6B6B63]">{item.numberOfBids || 0} bids</span>
                </>
              ) : (
                <>
                  <span className="text-[8px] font-['JetBrains_Mono',monospace] font-bold text-[#6B6B63] uppercase tracking-wider">Price</span>
                  <p className="font-['JetBrains_Mono',monospace] font-bold text-[#A6812C]">R{displayPrice.toFixed(2)}</p>
                </>
              )}
            </div>
            <div className="flex items-center gap-1">
              {item.isAuction && isAuctionActive ? (
                <button
                  onClick={(e) => { e.stopPropagation();
                    const nextBid = (item.currentBid || item.startingBid || 0) + (item.bidIncrement || 100);
                    handlePlaceBid(item, nextBid);
                  }}
                  className="px-3 py-1.5 bg-[#7A1F2B] text-[#F4E9C9] text-[9px] font-['JetBrains_Mono',monospace] font-bold rounded hover:bg-[#5C1520] transition-colors flex items-center gap-1 border border-[#A6812C]"
                >
                  <GavelIcon size={12} /> Bid
                </button>
              ) : (
                <button
                  onClick={(e) => { e.stopPropagation(); handleAddToCart(item); }}
                  className="px-3 py-1.5 bg-[#A6812C] text-white text-[9px] font-['JetBrains_Mono',monospace] font-bold rounded hover:bg-[#8E6E25] transition-colors"
                >
                  Add
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (isLoading && items.length === 0) {
    return (
      <div className="min-h-screen bg-[#FDFCFA] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#A6812C]" size={40} />
      </div>
    );
  }

  const cartCount = cartItems.reduce((sum, item) => sum + (item.quantity || 1), 0);

  return (
    <div className="min-h-screen bg-[#FDFCFA] text-[#17171A] font-['Inter',sans-serif]">

      {/* DESKTOP HEADER */}
      <header className="hidden lg:flex sticky top-0 z-50 bg-white/95 backdrop-blur-xl border-b border-[#E8E0D8] flex-col">
        <div className="flex justify-between items-center py-2 px-8 border-b border-[#E8E0D8] text-[9px] font-['JetBrains_Mono',monospace] font-bold uppercase tracking-widest text-[#6B6B63]">
          <span>Fine Jewellery</span>
          <span>Certified &amp; Authenticated Pieces</span>
        </div>

        <div className="flex items-center justify-between h-20 px-8">
          <nav className="flex gap-8 items-center">
            {['Brands', 'Jewellery', 'Thrift', 'Designer'].map((link) => {
              let path = '/';
              if (link === 'Brands') path = '/';
              else if (link === 'Jewellery') path = '/jewellery';
              else if (link === 'Thrift') path = '/thrifts';
              else if (link === 'Designer') path = '/designer';

              return (
                <NavLink
                  key={link}
                  to={path}
                  className={({isActive}) => `text-[10px] font-['JetBrains_Mono',monospace] font-black uppercase tracking-[0.2em] hover:text-[#17171A] transition-colors whitespace-nowrap ${isActive ? 'text-[#17171A] border-b-2 border-[#A6812C] pb-0.5' : 'text-[#6B6B63]'}`}
                >
                  {link}
                </NavLink>
              );
            })}
          </nav>

          <div className="absolute left-1/2 -translate-x-1/2 flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
            <Gem size={28} className="text-[#A6812C]" />
            <h1 className="text-3xl font-['Cormorant_Garamond',serif] font-medium tracking-tight text-[#17171A]">
              Scoop <span className="italic text-[#A6812C]">&amp;</span> Stone
            </h1>
          </div>

          <div className="flex items-center gap-6">
            <NavLink to="/search" className="text-[#6B6B63] hover:text-[#17171A] transition-colors">
              <Search size={20} />
            </NavLink>

            <button onClick={() => window.dispatchEvent(new CustomEvent('open-messages'))} className="text-[#6B6B63] hover:text-[#17171A] transition-colors relative">
              <MessageCircle size={20} />
            </button>

            <button onClick={() => window.dispatchEvent(new CustomEvent('open-notifications'))} className="text-[#6B6B63] hover:text-[#17171A] transition-colors relative">
              <Bell size={20} />
            </button>

            <button onClick={() => window.dispatchEvent(new CustomEvent('open-creation-menu'))} className="text-[#6B6B63] hover:text-[#17171A] transition-colors">
              <Plus size={20} strokeWidth={2} />
            </button>

            <NavLink to="/profile" className="text-[10px] font-['JetBrains_Mono',monospace] font-black uppercase tracking-widest text-[#6B6B63] hover:text-[#17171A] transition-colors">
              {currentUser ? `Account (${userProfile?.cred || 0})` : 'Login'}
            </NavLink>

            <button onClick={() => setIsCartOpen(true)} className={`flex items-center gap-2 transition-colors ${cartCount > 0 ? 'text-[#17171A]' : 'text-[#6B6B63] hover:text-[#17171A]'}`}>
              <ShoppingBag size={20} />
              <span className="text-[10px] font-['JetBrains_Mono',monospace] font-black">({cartCount})</span>
            </button>
          </div>
        </div>
      </header>

      {/* MOBILE HEADER */}
      <header className="lg:hidden sticky top-0 z-40 bg-white/95 backdrop-blur-xl border-b border-[#E8E0D8]">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
            <Gem size={20} className="text-[#A6812C]" />
            <h1 className="text-lg font-['Cormorant_Garamond',serif] font-medium text-[#17171A]">
              Scoop <span className="italic text-[#A6812C]">&amp;</span> Stone
            </h1>
          </div>

          <div className="flex gap-4 items-center text-[#6B6B63]">
            <button onClick={() => window.dispatchEvent(new CustomEvent('open-messages'))} className="hover:text-[#17171A] transition-colors relative">
              <MessageCircle size={20} />
            </button>
            <button onClick={() => window.dispatchEvent(new CustomEvent('open-notifications'))} className="hover:text-[#17171A] transition-colors relative">
              <Bell size={20} />
            </button>
            <button onClick={() => setIsCartOpen(true)} className="hover:text-[#17171A] transition-colors relative">
              <ShoppingBag size={20} />
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-[#A6812C] text-white text-[8px] font-['JetBrains_Mono',monospace] font-black w-4 h-4 rounded-full flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </button>
          </div>
        </div>

        <div className="flex overflow-x-auto scrollbar-hide border-t border-[#E8E0D8]">
          {['Brands', 'Jewellery', 'Thrift', 'Designer'].map((tab) => {
            let path = '/';
            let isActive = false;

            if (tab === 'Brands') { path = '/'; isActive = location.pathname === '/'; }
            else if (tab === 'Jewellery') { path = '/jewellery'; isActive = location.pathname === '/jewellery'; }
            else if (tab === 'Thrift') { path = '/thrifts'; isActive = location.pathname === '/thrifts'; }
            else if (tab === 'Designer') { path = '/designer'; isActive = location.pathname === '/designer'; }

            return (
              <NavLink
                key={tab}
                to={path}
                className={({isActive: isNavActive}) => {
                  const active = isNavActive || isActive;
                  return `flex-1 text-center px-2 py-2.5 text-[9px] font-['JetBrains_Mono',monospace] font-black uppercase tracking-wider transition-all duration-200 whitespace-nowrap ${
                    active ? 'text-[#17171A] border-b-2 border-[#A6812C]' : 'text-[#6B6B63] hover:text-[#17171A]'
                  }`;
                }}
              >
                {tab}
              </NavLink>
            );
          })}
        </div>
      </header>

      {/* MAIN CONTENT */}
      <div className="lg:pt-0 pt-0 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* BANNER — studio entry point */}
        <div className="relative w-full h-48 sm:h-56 rounded-2xl overflow-hidden bg-[#17171A] border border-[#A6812C]/20 shadow-xl mb-8">
          <div className="absolute inset-0 flex items-center justify-center opacity-10">
            <Gem size={140} className="text-[#A6812C]" />
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-transparent" />
          <div className="relative z-10 h-full flex flex-col justify-center px-8 sm:px-12">
            <div className="flex items-center gap-3 mb-2">
              <WaxSeal size={36} />
              <span className="text-[#A6812C] text-[10px] font-['JetBrains_Mono',monospace] font-black uppercase tracking-widest">
                Fine Jewellery
              </span>
            </div>

            <h2 className="text-2xl sm:text-3xl font-['Cormorant_Garamond',serif] font-medium text-white tracking-tight leading-tight">
              Design <span className="text-[#A6812C]">custom</span> jewellery
            </h2>

            <p className="text-white/50 text-xs sm:text-sm mt-1 max-w-md font-['Inter',sans-serif]">
              Build a bespoke piece bead-by-bead — pearls, gemstones, metal and charms — from South Africa's finest independent jewellers.
            </p>

            <div className="flex items-center gap-3 mt-5">
              <button
                onClick={() => setIsStudioOpen(true)}
                className="flex items-center gap-2 px-6 py-3 bg-[#A6812C] hover:bg-[#8E6E25] text-white text-[11px] font-['JetBrains_Mono',monospace] font-black uppercase tracking-widest rounded-full transition-colors shadow-lg shadow-black/30"
              >
                <PenTool size={14} />
                Start designing
              </button>

              <button
                onClick={() => document.getElementById('jewellery-grid')?.scrollIntoView({ behavior: 'smooth' })}
                className="text-[10px] font-['JetBrains_Mono',monospace] font-black uppercase tracking-widest text-white/70 hover:text-white transition-colors"
              >
                or browse pieces →
              </button>
            </div>
          </div>
        </div>

        {/* Categories */}
        <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-4">
          {categories.map((cat) => (
            <button
              key={cat.name}
              onClick={() => setActiveCategory(cat.name)}
              className={`shrink-0 px-5 py-2.5 rounded-full text-[10px] font-['JetBrains_Mono',monospace] font-bold transition-all border ${
                activeCategory === cat.name
                  ? 'bg-[#17171A] text-white border-[#17171A] shadow-lg shadow-[#17171A]/10'
                  : 'bg-white text-[#17171A] border-[#E8E0D8] hover:border-[#A6812C]'
              } flex items-center gap-2`}
            >
              {cat.icon} {cat.name}
            </button>
          ))}
        </div>

        {/* Filter Bar */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 py-4 border-y border-[#E8E0D8] mb-6">
          <p className="text-[10px] font-['JetBrains_Mono',monospace] font-bold text-[#6B6B63] uppercase tracking-widest">
            {filteredItems.length} Pieces Found
          </p>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex gap-2">
              {[
                { id: 'all', label: 'All' },
                { id: 'auction', label: 'Auction' },
                { id: 'buy_now', label: 'Buy Now' },
              ].map((filter) => (
                <button
                  key={filter.id}
                  onClick={() => setActiveFilter(filter.id as any)}
                  className={`px-3 py-1.5 rounded-full text-[9px] font-['JetBrains_Mono',monospace] font-bold uppercase tracking-wider transition-all border ${
                    activeFilter === filter.id
                      ? 'bg-[#17171A] text-white border-[#17171A]'
                      : 'bg-white text-[#6B6B63] border-[#E8E0D8] hover:border-[#A6812C]'
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>
            <button
              onClick={() => setIsFilterOpen(true)}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-[#E8E0D8] hover:border-[#A6812C] rounded-full text-[10px] font-['JetBrains_Mono',monospace] font-black uppercase text-[#6B6B63] hover:text-[#17171A] transition-all"
            >
              <Filter size={14} /> Filter
            </button>
          </div>
        </div>

        {/* Grid */}
        <div id="jewellery-grid">
          {filteredItems.length === 0 ? (
            <div className="text-center py-20 text-[#6B6B63] font-['JetBrains_Mono',monospace] font-bold uppercase tracking-widest text-sm">
              <Gem size={32} className="mx-auto mb-4 opacity-30" />
              No jewellery pieces found matching your criteria.
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6">
              {filteredItems.map(renderProductCard)}
            </div>
          )}
        </div>
      </div>

      {/* Filter Modal */}
      {isFilterOpen && (
        <div className="fixed inset-0 z-50 flex flex-col justify-end">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setIsFilterOpen(false)} />
          <div className="bg-white w-full rounded-t-[2rem] p-6 max-h-[85vh] overflow-y-auto relative z-10 animate-in slide-in-from-bottom-full duration-300">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-['Cormorant_Garamond',serif] font-medium text-[#17171A]">Refine</h2>
              <button onClick={() => setIsFilterOpen(false)} className="p-2 bg-[#FAF8F3] rounded-full hover:bg-[#F0EDE6] transition-colors">
                <X size={20} />
              </button>
            </div>

            <div className="space-y-6">
              <div>
                <h3 className="text-[10px] font-['JetBrains_Mono',monospace] font-black text-[#6B6B63] uppercase tracking-widest mb-3">Price Range</h3>
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <label className="text-[8px] text-[#6B6B63] font-['JetBrains_Mono',monospace]">Min</label>
                    <input type="number" value={filters.minPrice} onChange={e => setFilters({...filters, minPrice: parseInt(e.target.value) || 0})} className="w-full p-2 border border-[#E8E0D8] rounded-lg text-sm font-bold outline-none focus:border-[#A6812C] font-['JetBrains_Mono',monospace] text-[#17171A]" />
                  </div>
                  <div className="flex-1">
                    <label className="text-[8px] text-[#6B6B63] font-['JetBrains_Mono',monospace]">Max</label>
                    <input type="number" value={filters.maxPrice} onChange={e => setFilters({...filters, maxPrice: parseInt(e.target.value) || 50000})} className="w-full p-2 border border-[#E8E0D8] rounded-lg text-sm font-bold outline-none focus:border-[#A6812C] font-['JetBrains_Mono',monospace] text-[#17171A]" />
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-[10px] font-['JetBrains_Mono',monospace] font-black text-[#6B6B63] uppercase tracking-widest mb-3">Metal Type</h3>
                <div className="flex flex-wrap gap-2">
                  {metalTypes.map(metal => (
                    <button
                      key={metal}
                      onClick={() => setFilters({...filters, metalType: filters.metalType === metal ? '' : metal})}
                      className={`px-3 py-1.5 rounded-full text-[10px] font-['JetBrains_Mono',monospace] font-bold transition-all border ${
                        filters.metalType === metal
                          ? 'bg-[#17171A] text-white border-[#17171A]'
                          : 'bg-white text-[#6B6B63] border-[#E8E0D8] hover:border-[#A6812C]'
                      }`}
                    >
                      {metal}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-[10px] font-['JetBrains_Mono',monospace] font-black text-[#6B6B63] uppercase tracking-widest mb-3">Gemstone</h3>
                <div className="flex flex-wrap gap-2">
                  {gemstones.map(gemstone => (
                    <button
                      key={gemstone}
                      onClick={() => setFilters({...filters, gemstone: filters.gemstone === gemstone ? '' : gemstone})}
                      className={`px-3 py-1.5 rounded-full text-[10px] font-['JetBrains_Mono',monospace] font-bold transition-all border ${
                        filters.gemstone === gemstone
                          ? 'bg-[#17171A] text-white border-[#17171A]'
                          : 'bg-white text-[#6B6B63] border-[#E8E0D8] hover:border-[#A6812C]'
                      }`}
                    >
                      {gemstone}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-[10px] font-['JetBrains_Mono',monospace] font-black text-[#6B6B63] uppercase tracking-widest mb-3">Condition</h3>
                <div className="flex flex-wrap gap-2">
                  {conditions.map(condition => (
                    <button
                      key={condition}
                      onClick={() => setFilters({...filters, condition: filters.condition === condition ? '' : condition})}
                      className={`px-3 py-1.5 rounded-full text-[10px] font-['JetBrains_Mono',monospace] font-bold transition-all border ${
                        filters.condition === condition
                          ? 'bg-[#17171A] text-white border-[#17171A]'
                          : 'bg-white text-[#6B6B63] border-[#E8E0D8] hover:border-[#A6812C]'
                      }`}
                    >
                      {condition}
                    </button>
                  ))}
                </div>
              </div>

              <button onClick={() => setIsFilterOpen(false)} className="w-full py-4 bg-[#A6812C] text-white font-['Inter',sans-serif] font-bold rounded-xl hover:bg-[#8E6E25] transition-colors">
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Product Detail Modal — for regular products only */}
      {selectedItem && (
        <JewelleryProductDetailModal
          item={selectedItem}
          isOpen={!!selectedItem}
          onClose={() => setSelectedItem(null)}
          onToggleWishlist={onToggleWishlist}
          isWishlisted={wishlist.includes(selectedItem.id)}
          onAddToCart={handleAddToCart}
          onBuyNow={handleBuyNow}
          onViewProfile={handleViewJeweller}
          onSendMessage={onSendMessage}
          currentUser={currentUser}
          userProfile={userProfile}
          cartItems={cartItems}
          requireAuth={(fn, message) => {
            if (!currentUser) {
              alert(message || 'Please log in to continue.');
              return;
            }
            fn();
          }}
        />
      )}

      {/* Jeweller Profile Modal */}
      {viewingJeweller && (
        <JewelleryProfileModal
          isOpen={!!viewingJeweller}
          onClose={() => setViewingJeweller(null)}
          brand={viewingJeweller}
          products={jewellerProducts}
          onSelectProduct={(product: any) => {
            setViewingJeweller(null);
            setTimeout(() => { setSelectedItem(product); }, 300);
          }}
          onSendMessage={onSendMessage}
        />
      )}

      {/* Cart Overlay */}
      {isCartOpen && (
        <CartOverlay
          isOpen={isCartOpen}
          onClose={() => setIsCartOpen(false)}
          cart={cartItems}
          updateQuantity={updateQuantity}
          remove={removeFromCart}
          checkout={() => {
            setIsCartOpen(false);
            setIsPaymentOpen(true);
          }}
        />
      )}

      {/* Payment Modal — stays last */}
      {isPaymentOpen && (
        <PaymentModal
          isOpen={isPaymentOpen}
          onClose={() => {
            setIsPaymentOpen(false);
            setPendingBuyNowItem(null);
          }}
          cart={cartItems}
          onCheckoutComplete={handleCheckoutComplete}
          profile={userProfile}
        />
      )}

      {/* 🟢 STUDIO — full-screen overlay. Title: "Infinite Stones". Icon: Gem. */}
      {isStudioOpen && (
        <div className="fixed inset-0 z-[100] bg-white flex flex-col">
          <header className="flex items-center justify-between px-6 py-4 border-b border-[#E8E0D8] bg-white/95 backdrop-blur-xl">
            <div className="flex items-center gap-3">
              <Gem size={22} className="text-[#A6812C]" />
              <div>
                <h2 className="font-['Cormorant_Garamond',serif] text-xl font-medium text-[#17171A]">
                  Infinite Stones
                </h2>
                <p className="text-[10px] font-['JetBrains_Mono',monospace] font-bold uppercase tracking-widest text-[#6B6B63]">
                  Bespoke · Handcrafted · Certified
                </p>
              </div>
            </div>
            <button
              onClick={() => setIsStudioOpen(false)}
              className="p-2 rounded-full hover:bg-[#FAF8F3] transition-colors"
              aria-label="Close studio"
            >
              <X size={20} className="text-[#6B6B63]" />
            </button>
          </header>
          <div className="flex-1 min-h-0">
            <JewelleryStudio
              initialPiece="necklace"
              onAddToCart={handleStudioAddToCart}
              onBuyNow={handleStudioBuyNow}
            />
          </div>
        </div>
      )}
    </div>
  );
};

/* AUCTION BADGE */
const AuctionBadge = ({ endsAt }: { endsAt: number }) => {
  const [timeLeft, setTimeLeft] = useState(endsAt - Date.now());

  useEffect(() => {
    const interval = setInterval(() => {
      setTimeLeft(endsAt - Date.now());
    }, 1000);
    return () => clearInterval(interval);
  }, [endsAt]);

  if (timeLeft <= 0) return <span className="text-[#6B6B63] text-[8px] font-['JetBrains_Mono',monospace] font-bold">Ended</span>;

  const hours = Math.floor(timeLeft / (1000 * 60 * 60));
  const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));

  if (hours > 24) {
    const days = Math.floor(hours / 24);
    return <span className="text-[#C9A96E] text-[8px] font-['JetBrains_Mono',monospace] font-bold">{days}d {hours % 24}h</span>;
  }
  if (hours > 0) {
    return <span className="text-[#C9A96E] text-[8px] font-['JetBrains_Mono',monospace] font-bold">{hours}h {minutes}m</span>;
  }
  return <span className="text-[#7A1F2B] text-[8px] font-['JetBrains_Mono',monospace] font-bold animate-pulse">{minutes}m</span>;
};

export default JewelleryView;